import { type FunctionComponent } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { formatDistanceToNow, toISOString } from "shared/lib/utils";

import type { timelinesInSuperSplashHomeV2LiveQuery } from "scmp-app/queries/__generated__/timelinesInSuperSplashHomeV2LiveQuery.graphql";

import { Container, DateContainer, SnippetTitle, Timeline } from "./styles";

type Props = {
  className?: string;
  entityUuid: string;
  urlAlias?: string;
};

export const HomeV2Timelines: FunctionComponent<Props> = ({ className, entityUuid, urlAlias }) => {
  const queryReference = useLazyLoadQuery<timelinesInSuperSplashHomeV2LiveQuery>(
    graphql`
      query timelinesInSuperSplashHomeV2LiveQuery($entityUuid: String!) {
        liveArticle(filter: { entityUuid: $entityUuid }) {
          liveContents {
            entityId
            title
            publishedDate
          }
        }
      }
    `,
    {
      entityUuid,
    },
    { fetchPolicy: "store-or-network" },
  );

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const contentNumber = isMobile ? 3 : 5;

  const slicedLiveContents = (queryReference?.liveArticle?.liveContents ?? [])
    .toSorted((a, b) => (b?.publishedDate ?? 0) - (a?.publishedDate ?? 0))
    .slice(0, contentNumber);

  return (
    <Container className={className}>
      {slicedLiveContents.map(liveSnippet => {
        if (liveSnippet) {
          return (
            <Timeline
              hash={`#live-${liveSnippet.entityId}`}
              key={liveSnippet.entityId}
              pathname={urlAlias ?? ""}
            >
              <DateContainer>
                {liveSnippet.publishedDate ? (
                  <time dateTime={toISOString(liveSnippet.publishedDate)} suppressHydrationWarning>
                    {formatDistanceToNow(liveSnippet.publishedDate)}
                  </time>
                ) : null}
              </DateContainer>
              <SnippetTitle>{liveSnippet.title}</SnippetTitle>
            </Timeline>
          );
        }
      })}
    </Container>
  );
};

HomeV2Timelines.displayName = "HomeV2Timelines";
